<script setup lang="ts">
import { computed } from 'vue';
import { Send } from 'lucide-vue-next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { clientResponseStatusLabel } from '~/lib/taskClientResponse';
import type { TaskClientResponseMessage, TaskClientResponseStatus } from '~/types/models';

const props = withDefaults(defineProps<{
    promptMessage?: string;
    responseStatus?: TaskClientResponseStatus | string;
    thread?: TaskClientResponseMessage[];
    emptyState?: string;
    showPromptMessage?: boolean;
    showFollowUpComposer?: boolean;
    followUpMessage?: string;
    requestingFollowUp?: boolean;
    followUpLabel?: string;
    followUpPlaceholder?: string;
}>(), {
    promptMessage: '',
    responseStatus: 'waiting',
    thread: () => [],
    emptyState: 'No client response thread yet. Once the conversation starts, it will show here.',
    showPromptMessage: false,
    showFollowUpComposer: false,
    followUpMessage: '',
    requestingFollowUp: false,
    followUpLabel: 'Ask for more information',
    followUpPlaceholder: 'Clarify what you still need from the client, such as missing files, extra details, or a revised answer.',
});

const emit = defineEmits<{
    (e: 'update:followUpMessage', value: string): void;
    (e: 'request-follow-up'): void;
}>();

const normalizedPromptMessage = computed(() => String(props.promptMessage || '').trim());
const threadEntries = computed(() => Array.isArray(props.thread) ? props.thread : []);
const responseStatusLabel = computed(() => clientResponseStatusLabel({ clientResponseStatus: props.responseStatus as TaskClientResponseStatus }));

function formatTimestamp(timestamp: unknown): string {
    if (!timestamp) return 'Unknown';

    try {
        const date = typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp
            ? (timestamp as { toDate: () => Date }).toDate()
            : new Date(String(timestamp));

        if (Number.isNaN(date.getTime())) {
            return 'Unknown';
        }

        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return 'Unknown';
    }
}
</script>

<template>
    <div class="space-y-3 rounded-xl border border-border/70 bg-background/76 px-3 py-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
            <p class="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground/85">Request thread</p>
            <Badge variant="outline">{{ responseStatusLabel }}</Badge>
        </div>

        <div
            v-if="showPromptMessage && normalizedPromptMessage"
            class="rounded-xl border border-border/60 bg-primary/5 px-3 py-3"
        >
            <div class="flex flex-wrap items-center justify-between gap-2">
                <p class="text-sm font-medium text-foreground">Team request</p>
                <p class="text-xs uppercase tracking-[0.14em] text-muted-foreground/80">Request</p>
            </div>
            <p class="mt-2 whitespace-pre-wrap text-sm text-foreground">{{ normalizedPromptMessage }}</p>
        </div>

        <div v-if="threadEntries.length > 0" class="space-y-3">
            <div
                v-for="entry in threadEntries"
                :key="entry.id"
                class="rounded-xl border border-border/60 px-3 py-3"
                :class="entry.authorType === 'tenant' ? 'bg-primary/5' : 'bg-background/80'"
            >
                <div class="flex flex-wrap items-center justify-between gap-2">
                    <p class="text-sm font-medium text-foreground">{{ entry.authorName || (entry.authorType === 'tenant' ? 'Team' : 'Client') }}</p>
                    <p class="text-xs uppercase tracking-[0.14em] text-muted-foreground/80">{{ entry.authorType === 'tenant' ? 'Team' : 'Client' }}</p>
                </div>
                <p class="mt-2 whitespace-pre-wrap text-sm text-foreground">{{ entry.body }}</p>
                <p class="mt-2 text-xs text-muted-foreground">{{ formatTimestamp(entry.createdAt) }}</p>
            </div>
        </div>

        <div
            v-else
            class="rounded-xl border border-dashed border-border/70 px-3 py-4 text-sm text-muted-foreground"
        >
            {{ emptyState }}
        </div>

        <div v-if="showFollowUpComposer" class="space-y-2 border-t border-border/60 pt-3">
            <label class="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground/85">Ask for more information</label>
            <Textarea
                :model-value="followUpMessage"
                rows="3"
                :placeholder="followUpPlaceholder"
                @update:model-value="(value) => emit('update:followUpMessage', String(value || ''))"
            />
            <div class="flex justify-end">
                <Button type="button" size="sm" :disabled="requestingFollowUp || !String(followUpMessage || '').trim()" @click="emit('request-follow-up')">
                    <Send class="mr-2 size-4" />
                    {{ requestingFollowUp ? 'Sending...' : followUpLabel }}
                </Button>
            </div>
        </div>
    </div>
</template>
